import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

const TEXT_SIZES = {
    M: 'cook-mode--text-m',
    L: 'cook-mode--text-l',
    XL: 'cook-mode--text-xl',
};

const formatSeconds = (seconds) => {
    const mins = Math.floor(seconds / 60)
        .toString()
        .padStart(2, '0');
    const secs = (seconds % 60)
        .toString()
        .padStart(2, '0');
    return `${mins}:${secs}`;
};

const extractStepText = (step) => {
    if (typeof step === 'string') return step.trim();
    if (!step || typeof step !== 'object') return '';
    return String(step.text || step.instruction || step.step || step.direction || '').trim();
};

const normalizeSteps = (recipe) => {
    const candidates = [
        recipe?.instructions,
        recipe?.steps,
        recipe?.directions,
        recipe?.method,
    ];

    for (const candidate of candidates) {
        if (Array.isArray(candidate)) {
            const steps = candidate.map(extractStepText).filter(Boolean);
            if (steps.length > 0) return steps;
        }

        if (typeof candidate === 'string') {
            const steps = candidate
                .split(/\r?\n+/)
                .map((line) => line.replace(/^\s*\d+[.)-]?\s*/, '').trim())
                .filter(Boolean);

            if (steps.length > 0) return steps;
        }
    }

    return [];
};

const isLikelyRecipe = (value) => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    return Boolean(value.title || value.name || value.instructions || value.steps || value.directions);
};

function CookMode({ recipe: recipeProp, recipeData }) {
    const location = useLocation();

    const recipe = useMemo(() => {
        if (recipeProp) return recipeProp;
        if (recipeData) return recipeData;
        if (isLikelyRecipe(location.state)) return location.state;
        if (isLikelyRecipe(location.state?.recipe)) return location.state.recipe;
        if (isLikelyRecipe(location.state?.recipeData)) return location.state.recipeData;
        return null;
    }, [location.state, recipeData, recipeProp]);

    const steps = useMemo(() => normalizeSteps(recipe), [recipe]);
    const title = recipe?.title || recipe?.name || 'Cook Mode';

    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [textSize, setTextSize] = useState('L');
    const [focusMode, setFocusMode] = useState(false);
    const [isTimerRunning, setIsTimerRunning] = useState(false);
    const [stepTimes, setStepTimes] = useState({});

    useEffect(() => {
        if (currentStepIndex >= steps.length) {
            setCurrentStepIndex(Math.max(steps.length - 1, 0));
        }
    }, [currentStepIndex, steps.length]);

    useEffect(() => {
        setIsTimerRunning(false);
    }, [currentStepIndex]);

    useEffect(() => {
        if (!isTimerRunning || steps.length === 0) return undefined;

        const intervalId = setInterval(() => {
            setStepTimes((prev) => ({
                ...prev,
                [currentStepIndex]: (prev[currentStepIndex] || 0) + 1,
            }));
        }, 1000);

        return () => clearInterval(intervalId);
    }, [currentStepIndex, isTimerRunning, steps.length]);

    if (!recipe || steps.length === 0) {
        return (
            <div className="page cook-mode-page">
                <section className="card cook-mode cook-mode--empty" aria-live="polite">
                    <h1>Cook Mode</h1>
                    <p>No step-by-step recipe data is available.</p>
                    <p className="text-muted mb-0">
                        Pass a recipe prop, or navigate with route state like
                        {' '}<code>{`{ state: { recipe } }`}</code>.
                    </p>
                </section>
            </div>
        );
    }

    const totalSteps = steps.length;
    const currentStep = steps[currentStepIndex];
    const currentSeconds = stepTimes[currentStepIndex] || 0;
    const progressPercent = Math.round(((currentStepIndex + 1) / totalSteps) * 100);
    const isLastStep = currentStepIndex === totalSteps - 1;

    return (
        <div className="page cook-mode-page">
            <section className={`card cook-mode ${TEXT_SIZES[textSize]} ${focusMode ? 'cook-mode--focus' : ''}`}>
                <header className="cook-mode__header">
                    <p className="cook-mode__eyebrow">Senior-friendly cook mode</p>
                    <h1>{title}</h1>
                    <div className="cook-mode__progressbar" role="progressbar" aria-valuemin={0} aria-valuemax={100} aria-valuenow={progressPercent}>
                        <span style={{ width: `${progressPercent}%` }} />
                    </div>
                </header>

                <div className="cook-mode__status" aria-live="polite">
                    <span className="cook-mode__progress">Step {currentStepIndex + 1} of {totalSteps}</span>
                    <div className="cook-mode__text-size" role="group" aria-label="Text size">
                        {Object.keys(TEXT_SIZES).map((size) => (
                            <button
                                key={size}
                                type="button"
                                className={`btn ${textSize === size ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setTextSize(size)}
                                aria-pressed={textSize === size}
                            >
                                {size}
                            </button>
                        ))}
                        <button
                            type="button"
                            className={`btn ${focusMode ? 'btn-primary' : 'btn-secondary'}`}
                            onClick={() => setFocusMode((prev) => !prev)}
                            aria-pressed={focusMode}
                        >
                            Focus
                        </button>
                    </div>
                </div>

                <article className="cook-mode__step" aria-label={`Step ${currentStepIndex + 1}`}>
                    {currentStep}
                </article>

                <div className="cook-mode__timer" role="group" aria-label="Step timer controls">
                    <div className="cook-mode__time" aria-live="polite">Timer: {formatSeconds(currentSeconds)}</div>
                    <div className="cook-mode__timer-buttons">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setIsTimerRunning((prev) => !prev)}
                        >
                            {isTimerRunning ? 'Stop' : 'Start'}
                        </button>
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => {
                                setIsTimerRunning(false);
                                setStepTimes((prev) => ({ ...prev, [currentStepIndex]: 0 }));
                            }}
                        >
                            Reset
                        </button>
                    </div>
                </div>

                <nav className="cook-mode__nav" aria-label="Step navigation">
                    <button
                        type="button"
                        className="btn btn-secondary cook-mode__nav-btn"
                        onClick={() => setCurrentStepIndex((prev) => Math.max(prev - 1, 0))}
                        disabled={currentStepIndex === 0}
                    >
                        Previous
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary cook-mode__nav-btn"
                        onClick={() => setCurrentStepIndex((prev) => Math.min(prev + 1, totalSteps - 1))}
                        disabled={currentStepIndex === totalSteps - 1}
                    >
                        Next
                    </button>
                </nav>
                {isLastStep && (
                    <div className="alert alert-success mt-3 mb-0" role="status">
                        Final step reached. You can reset the timer or go back if you want to review previous steps.
                    </div>
                )}
            </section>
        </div>
    );
}

export default CookMode;
